/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  FormControl,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  TablePagination,
  IconButton,
  Button,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
} from '@mui/material';
import 'leaflet/dist/leaflet.css';
import { FaTrashAlt, FaEdit, FaInfo } from 'react-icons/fa';
import AdminLokasi from './AdminLokasi';

import '../../../componen/css/Dashboard.css';
import axios from 'axios';
import useSWR, { mutate } from 'swr';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { apiHost } from '../../../utils/apiHost';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = apiHost().replace(/^https?:\/\//, "");
  return `${protocol}://${baseUrl}`;
};

const fetcher = (url) => axios.get(url, { withCredentials: true }).then((res) => res.data);

const ActionButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

export const LokasiPresensi = () => {
  const { user } = useSelector((state) => state.auth);
  const [selectedCabang, setSelectedCabang] = useState('');
  const [selectedTanggal, setSelectedTanggal] = useState(new Date().toISOString().slice(0, 10)); // Default tanggal hari ini
  const [absensiData, setAbsensiData] = useState([]);
  const [cabang, setCabang] = useState(null);
  const [error, setError] = useState('');

  // SWR untuk mengambil data daftar cabang
  const { data: cabangList, error: cabangError } = useSWR(user?.role === 'superadmin' ? `${getApiBaseUrl()}/cabang` : null, fetcher);

  // URL API dinamis berdasarkan cabang dan tanggal yang dipilih
  const apiUrl = selectedCabang && selectedTanggal
    ? `${getApiBaseUrl()}/absensilokasi/${selectedCabang}/${selectedTanggal}`
    : null;

  // SWR untuk mengambil data absensi berdasarkan URL API yang dinamis
  const { data: lokasiAbsensiResponse, error: lokasiAbsensiError, isLoading } = useSWR(apiUrl, fetcher);

  useEffect(() => {
    if (user?.role === 'admin') {
      // Server mengirim cabanguuid (huruf kecil); cabangUuid tidak pernah ada sehingga admin tidak melihat data.
      const cabangAdmin = user.cabanguuid || user.cabangUuid;
      setSelectedCabang(cabangAdmin);
      const fetchCabangData = async () => {
        try {
          const response = await axios.get(`${getApiBaseUrl()}/cabang/${cabangAdmin}`, { withCredentials: true });
          setCabang(response.data.data);
        } catch (err) {
          console.error("Error fetching admin's branch data:", err);
        }
      };
      fetchCabangData();
    }
  }, [user]);

  useEffect(() => {
    if (lokasiAbsensiResponse) {
      setAbsensiData(lokasiAbsensiResponse.data);
    }
    if (lokasiAbsensiError) {
      setError('Gagal memuat data lokasi absensi.');
      console.error(lokasiAbsensiError);
    }
  }, [lokasiAbsensiResponse, lokasiAbsensiError]);

  useEffect(() => {
    if (cabangList && user?.role === 'superadmin') {
      const firstCabang = cabangList.data[0];
      if (firstCabang) {
        setSelectedCabang(firstCabang.uuid);
        setCabang(firstCabang);
      }
    }
  }, [cabangList, user]);

  const handleCabangChange = (event) => {
    const uuid = event.target.value;
    setSelectedCabang(uuid);
    if (cabangList) {
      const selected = cabangList.data.find(c => c.uuid === uuid);
      setCabang(selected);
    }
  };

  const handleTanggalChange = (event) => {
    setSelectedTanggal(event.target.value);
  };

  const parseCoordinates = (lokasi) => {
    if (!lokasi) return null;
    const [lat, lon] = lokasi.split(',').map(Number);
    if (isNaN(lat) || isNaN(lon)) return null;
    return { latitude: lat, longitude: lon };
  };

  if (cabangError) {
    return <Typography>Error loading cabang data.</Typography>;
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3, backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Lokasi Presensi Karyawan
      </Typography>

      <Card sx={{ mb: 3, padding: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Filter
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Pilih Cabang</InputLabel>
              <Select
                value={selectedCabang}
                label="Pilih Cabang"
                onChange={handleCabangChange}
                disabled={user?.role === 'admin' || !cabangList}
              >
                {user?.role === 'superadmin' && cabangList?.data.map((c) => (
                  <MenuItem key={c.uuid} value={c.uuid}>
                    {c.namacabang}
                  </MenuItem>
                ))}
                {user?.role === 'admin' && cabang && (
                  <MenuItem value={cabang.uuid}>{cabang.namacabang}</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Pilih Tanggal"
              type="date"
              fullWidth
              value={selectedTanggal}
              onChange={handleTanggalChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Card>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card sx={{ padding: 2 }}>
          {absensiData.length > 0 ? (
            <Grid container spacing={3}>
              {absensiData.map((absen) => {
                const lokasiMasuk = parseCoordinates(absen.lokasi_masuk);
                const lokasiPulang = parseCoordinates(absen.lokasi_keluar);
                const cabangData = absen.User?.Cabang;
                
                return (
                  <Grid item xs={12} md={6} key={absen.uuid}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6">{absen.User?.username}</Typography>
                        <Typography color="textSecondary">
                          Waktu Masuk: {absen.jam_masuk || 'Belum Absen'}
                        </Typography>
                        <Typography color="textSecondary">
                          Waktu Pulang: {absen.jam_keluar || 'Belum Pulang'}
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle1">Lokasi Masuk:</Typography>
                          {lokasiMasuk && cabangData ? (
                            <Box sx={{ mt: 1, border: '1px solid #ccc', borderRadius: '4px', overflow: 'hidden' }}>
                              <AdminLokasi
                                latitude={lokasiMasuk.latitude}
                                longitude={lokasiMasuk.longitude}
                                branchCoordinates={cabangData.koordinat}
                                branchRadius={cabangData.radius}
                              />
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary">Lokasi tidak tersedia.</Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Typography align="center" sx={{ mt: 4, mb: 4 }}>
              Tidak ada data presensi untuk tanggal ini pada cabang ini.
            </Typography>
          )}
        </Card>
      )}
    </Box>
  );
};

export default LokasiPresensi;